import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAdminStats,
  getAdminUsers,
  updateAdminUser,
  resetAdminUserPassword,
  deleteAdminUser,
} from '../services/adminService';
import {
  listIngestedEvidence,
  ingestRawEmailEvidence,
} from '../services/emailIngestionAdminService';
import { getClients, updateClient } from '../services/clientService';
import { getBusinesses } from '../services/businessService';
import './AdminPage.css';

const ROLES = ['admin', 'member', 'guest'];

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value ?? '—'}</div>
      <div className="admin-stat-label">{label}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [copiedUserId, setCopiedUserId] = useState('');

  // ── Shadow Communications / Ingestion State ──
  const [evidenceList, setEvidenceList] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceStatusFilter, setEvidenceStatusFilter] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  // Test Simulation Form State
  const [showSimulateForm, setShowSimulateForm] = useState(false);
  const [simProvider, setSimProvider] = useState('gmail');
  const [simFrom, setSimFrom] = useState('');
  const [simFromName, setSimFromName] = useState('');
  const [simTo, setSimTo] = useState('');
  const [simSubject, setSimSubject] = useState('');
  const [simBody, setSimBody] = useState('');
  const [simMsgId, setSimMsgId] = useState('');
  const [simInReplyTo, setSimInReplyTo] = useState('');
  const [simulating, setSimulating] = useState(false);

  // ── Legacy Client Migration State (DEC-022) ──
  const [legacyClients, setLegacyClients] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [selectedBizForClient, setSelectedBizForClient] = useState({});
  const [linkingClientId, setLinkingClientId] = useState('');

  const handleCopyUserId = async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setCopiedUserId(String(id));
      window.setTimeout(() => {
        setCopiedUserId((prev) => (prev === String(id) ? '' : prev));
      }, 1500);
    } catch (err) {
      console.error('Failed to copy user ID:', err);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await getAdminStats();
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to load admin stats:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminUsers({ search });
      setUsers(res.data.users || []);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const loadEvidence = useCallback(async () => {
    setEvidenceLoading(true);
    try {
      const params = {};
      if (evidenceStatusFilter) params.status = evidenceStatusFilter;
      const res = await listIngestedEvidence(params);
      setEvidenceList(res.data.evidence || []);
    } catch (err) {
      console.error('Failed to load email evidence:', err);
    } finally {
      setEvidenceLoading(false);
    }
  }, [evidenceStatusFilter]);

  const loadLegacyMigration = useCallback(async () => {
    setLegacyLoading(true);
    try {
      const [clientsRes, bizRes] = await Promise.all([
        getClients({ includeArchived: 'true' }),
        getBusinesses(),
      ]);
      const clientList = clientsRes.data.clients || [];
      const bizList = bizRes.data.businesses || [];
      setLegacyClients(clientList);
      setBusinesses(bizList);

      // Pre-select suggested business matches if name matches
      const initialMap = {};
      for (const c of clientList) {
        if (!c.relationshipBusinessId) {
          const match = bizList.find((b) =>
            b.name?.toLowerCase().trim() === c.name?.toLowerCase().trim()
          );
          if (match) {
            initialMap[c._id] = match._id;
          }
        }
      }
      setSelectedBizForClient((prev) => ({ ...initialMap, ...prev }));
    } catch (err) {
      console.error('Failed to load legacy clients or businesses:', err);
    } finally {
      setLegacyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 'users') {
      loadUsers();
    } else if (tab === 'email-gateway') {
      loadEvidence();
    } else if (tab === 'legacy-clients') {
      loadLegacyMigration();
    }
  }, [tab, loadUsers, loadEvidence, loadLegacyMigration]);

  const handleLinkClientToBusiness = async (clientDocId, targetBizId) => {
    if (!targetBizId) return;
    setLinkingClientId(clientDocId);
    try {
      await updateClient(clientDocId, { relationshipBusinessId: targetBizId });
      await loadLegacyMigration();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link client to business');
    } finally {
      setLinkingClientId('');
    }
  };

  const handleUnlinkClientFromBusiness = async (clientDocId) => {
    if (!window.confirm('Unlink this Client from its Business relationship? The Client record is preserved.')) return;
    setLinkingClientId(clientDocId);
    try {
      await updateClient(clientDocId, { relationshipBusinessId: null });
      await loadLegacyMigration();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink client');
    } finally {
      setLinkingClientId('');
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleRoleChange = async (userId, role) => {
    try {
      const res = await updateAdminUser(userId, { role });
      setUsers((prev) => prev.map((item) => (item._id === userId ? res.data.user : item)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update role');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      const res = await updateAdminUser(userId, { isActive });
      setUsers((prev) => prev.map((item) => (item._id === userId ? res.data.user : item)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteAdminUser(userId);
      setUsers((prev) => prev.filter((item) => item._id !== userId));
      setConfirmDeleteId(null);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    const nextPassword = resetPasswordValue.trim();
    if (nextPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      await resetAdminUserPassword(userId, nextPassword);
      setResetPasswordId(null);
      setResetPasswordValue('');
      alert('Password reset successfully');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleSimulateIngestion = async (e) => {
    e.preventDefault();
    if (!simFrom.trim()) return;
    setSimulating(true);
    try {
      const toAddresses = simTo.split(',').map((s) => s.trim()).filter(Boolean);
      await ingestRawEmailEvidence({
        provider: simProvider,
        providerMessageId: `sim-${Date.now()}`,
        messageIdHeader: simMsgId.trim() || `<sim-${Date.now()}@test.local>`,
        inReplyTo: simInReplyTo.trim() || undefined,
        fromAddress: simFrom.trim(),
        fromDisplayName: simFromName.trim(),
        toAddresses: toAddresses.length ? toAddresses : ['intake@rsmin.ca'],
        subject: simSubject.trim() || 'Simulated Test Message',
        rawBodyText: simBody.trim() || 'Simulated body text.',
      });
      alert('Simulated email ingested and normalized successfully.');
      setShowSimulateForm(false);
      setSimFrom('');
      setSimFromName('');
      setSimTo('');
      setSimSubject('');
      setSimBody('');
      setSimMsgId('');
      setSimInReplyTo('');
      await loadEvidence();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to ingest simulated email');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Admin Panel</h1>
        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === 'overview' ? 'active' : ''}`}
            onClick={() => setTab('overview')}
          >
            Overview
          </button>
          <button
            className={`admin-tab ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            Users
          </button>
          <button
            className={`admin-tab ${tab === 'email-gateway' ? 'active' : ''}`}
            onClick={() => setTab('email-gateway')}
          >
            Email Gateway (Shadow)
          </button>
          <button
            className={`admin-tab ${tab === 'legacy-clients' ? 'active' : ''}`}
            onClick={() => setTab('legacy-clients')}
          >
            Legacy Clients (DEC-022)
          </button>
        </div>
      </div>

      {tab === 'overview' && (
        <div className="admin-section">
          {!stats ? (
            <p className="admin-muted">Loading stats...</p>
          ) : (
            <div className="admin-stats-grid">
              <StatCard label="Total Users" value={stats.totalUsers} sub={`${stats.activeUsers} active`} />
              <StatCard label="Inactive Users" value={stats.inactiveUsers} />
              <StatCard label="Admins" value={stats.adminUsers} />
              <StatCard label="Spaces" value={stats.totalSpaces} />
              <StatCard label="Threads" value={stats.totalThreads} />
              <StatCard label="Messages" value={stats.totalMessages} />
              <StatCard label="Connections" value={stats.totalConnections} />
            </div>
          )}
        </div>
      )}

      {tab === 'users' && (
        <div className="admin-section">
          <div className="admin-users-toolbar">
            <input
              className="admin-search"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn-sm btn-secondary" onClick={loadUsers}>Refresh</button>
          </div>

          {error && <p className="admin-error">{error}</p>}

          {loading ? (
            <p className="admin-muted">Loading users...</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((item) => (
                    <tr key={item._id} className={!item.isActive ? 'row-inactive' : ''}>
                      <td>
                        <div className="admin-user-cell">
                          <div className="admin-user-avatar">{(item.name || '?')[0].toUpperCase()}</div>
                          <div>
                            <div className="admin-user-name">{item.name}</div>
                            <div className="admin-user-email">{item.email}</div>
                            <div className="admin-user-id-row">
                              <span className="admin-user-id">ID: {item._id}</span>
                              <button
                                type="button"
                                className="admin-copy-id-btn"
                                onClick={() => handleCopyUserId(item._id)}
                                title="Copy user ID"
                                aria-label={`Copy user ID for ${item.name || 'user'}`}
                              >
                                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                                  <path d="M9 9h10v12H9z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                  <path d="M5 3h10v4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                  <path d="M5 3v12h4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                                </svg>
                                <span>{copiedUserId === String(item._id) ? 'Copied' : 'Copy'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select
                          className={`admin-role-select role-${item.role}`}
                          value={item.role}
                          onChange={(e) => handleRoleChange(item._id, e.target.value)}
                          disabled={String(item._id) === String(user?._id)}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          className={`admin-status-btn ${item.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleActive(item._id, !item.isActive)}
                          disabled={String(item._id) === String(user?._id)}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="admin-muted">
                        {new Date(item.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td>
                        {String(item._id) !== String(user?._id) && (
                          resetPasswordId === item._id ? (
                            <div className="admin-inline-actions">
                              <input
                                type="password"
                                className="admin-password-input"
                                placeholder="New password..."
                                value={resetPasswordValue}
                                onChange={(e) => setResetPasswordValue(e.target.value)}
                                autoFocus
                              />
                              <button className="btn-sm btn-secondary" onClick={() => handleResetPassword(item._id)}>
                                Save
                              </button>
                              <button
                                className="btn-sm"
                                onClick={() => {
                                  setResetPasswordId(null);
                                  setResetPasswordValue('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : confirmDeleteId === item._id ? (
                            <span className="admin-confirm-row">
                              <span className="admin-muted">Sure?</span>
                              <button className="btn-danger-sm" onClick={() => handleDeleteUser(item._id)}>
                                Yes, delete
                              </button>
                              <button className="btn-sm" onClick={() => setConfirmDeleteId(null)}>
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <div className="admin-inline-actions">
                              <button
                                className="btn-sm btn-secondary"
                                onClick={() => {
                                  setConfirmDeleteId(null);
                                  setResetPasswordId(item._id);
                                  setResetPasswordValue('');
                                }}
                              >
                                Reset Password
                              </button>
                              <button className="btn-danger-sm" onClick={() => setConfirmDeleteId(item._id)}>
                                Delete
                              </button>
                            </div>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && !loading && (
                    <tr>
                      <td colSpan="5" className="admin-muted admin-empty-cell">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── SHADOW COMMUNICATIONS GATEWAY TAB ── */}
      {tab === 'email-gateway' && (
        <div className="admin-section">
          <div className="gateway-toolbar">
            <div>
              <h3>Shadow-Mode Email Ingestion Evidence</h3>
              <p className="admin-muted">
                Audit trail of raw external emails ingested into ComSapp, verification hashes &amp; normalization status.
              </p>
            </div>
            <div className="gateway-toolbar-actions">
              <select
                className="form-select form-select-sm"
                value={evidenceStatusFilter}
                onChange={(e) => setEvidenceStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="normalized">Normalized</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <button
                className="btn-sm btn-secondary"
                onClick={() => setShowSimulateForm((v) => !v)}
              >
                {showSimulateForm ? '✕ Close Form' : '+ Simulate Inbound Email'}
              </button>
            </div>
          </div>

          {showSimulateForm && (
            <form onSubmit={handleSimulateIngestion} className="simulate-ingest-card">
              <h4>Test Ingest Simulated Email (Shadow Mode)</h4>
              <div className="form-row">
                <select
                  className="form-select form-select-sm"
                  value={simProvider}
                  onChange={(e) => setSimProvider(e.target.value)}
                >
                  <option value="gmail">Provider: Gmail</option>
                  <option value="m365">Provider: Microsoft 365</option>
                  <option value="smtp">Provider: Standard SMTP</option>
                </select>
                <input
                  className="form-input"
                  placeholder="From Address (e.g. partner@regulator.gov) *"
                  value={simFrom}
                  onChange={(e) => setSimFrom(e.target.value)}
                  required
                />
                <input
                  className="form-input"
                  placeholder="From Display Name"
                  value={simFromName}
                  onChange={(e) => setSimFromName(e.target.value)}
                />
                <input
                  className="form-input"
                  placeholder="To Addresses (e.g. consultation@rsmin.ca)"
                  value={simTo}
                  onChange={(e) => setSimTo(e.target.value)}
                />
              </div>
              <div className="form-row">
                <input
                  className="form-input"
                  placeholder="Subject"
                  value={simSubject}
                  onChange={(e) => setSimSubject(e.target.value)}
                />
                <input
                  className="form-input form-input-sm"
                  placeholder="In-Reply-To Message-ID (for threading)"
                  value={simInReplyTo}
                  onChange={(e) => setSimInReplyTo(e.target.value)}
                />
              </div>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Message Body Text..."
                value={simBody}
                onChange={(e) => setSimBody(e.target.value)}
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={simulating}>
                {simulating ? 'Ingesting...' : 'Ingest and Normalize'}
              </button>
            </form>
          )}

          {evidenceLoading ? (
            <p className="admin-muted">Loading ingested email records...</p>
          ) : evidenceList.length === 0 ? (
            <p className="admin-muted">No ingested email records found.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>From / Correspondent</th>
                  <th>Subject</th>
                  <th>To Destination</th>
                  <th>Status</th>
                  <th>Received</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {evidenceList.map((ev) => (
                  <tr key={ev._id}>
                    <td>
                      <strong>{ev.fromAddress}</strong>
                      {ev.resolvedSenderExternalIdentityId && (
                        <div className="identity-tag">External Identity</div>
                      )}
                    </td>
                    <td>{ev.subject || '(no subject)'}</td>
                    <td>
                      {(ev.resolvedToAddresses || []).map((r, i) => (
                        <span key={i} className="target-badge">{r.targetType || 'ext'}: {r.address}</span>
                      ))}
                    </td>
                    <td><span className={`status-pill ${ev.normalizationStatus}`}>{ev.normalizationStatus}</span></td>
                    <td className="admin-muted">{new Date(ev.receivedAt || ev.importedAt).toLocaleString()}</td>
                    <td>
                      <button className="btn btn-xs btn-ghost" onClick={() => setSelectedEvidence(ev)}>
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Evidence Details Modal */}
          {selectedEvidence && (
            <div className="modal-backdrop" onClick={() => setSelectedEvidence(null)}>
              <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div>
                    <h3>Ingested Evidence Record</h3>
                    <p className="modal-sub">Provider ID: {selectedEvidence.providerMessageId || 'N/A'}</p>
                  </div>
                  <button className="btn-icon" onClick={() => setSelectedEvidence(null)}>✕</button>
                </div>
                <div className="modal-body">
                  <div className="evidence-detail-grid">
                    <div><span>From:</span> <strong>{selectedEvidence.fromAddress}</strong></div>
                    <div><span>Subject:</span> <strong>{selectedEvidence.subject}</strong></div>
                    <div><span>Message-ID:</span> <code>{selectedEvidence.messageIdHeader || 'None'}</code></div>
                    <div><span>In-Reply-To:</span> <code>{selectedEvidence.inReplyTo || 'None'}</code></div>
                    <div><span>SHA-256 Hash:</span> <code className="hash-code">{selectedEvidence.contentHash}</code></div>
                    <div><span>Normalized Thread ID:</span> <code>{selectedEvidence.normalizedThreadId || 'None'}</code></div>
                  </div>
                  <div className="raw-body-box">
                    <h4>Raw Body Text</h4>
                    <pre>{selectedEvidence.rawBodyText || selectedEvidence.rawBodyHtml || '(empty)'}</pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-ghost" onClick={() => setSelectedEvidence(null)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'legacy-clients' && (
        <div className="admin-section">
          <div className="migration-banner">
            <div>
              <h3>DEC-022 Legacy Client Linking Tool</h3>
              <p className="admin-muted">
                In ComSapp V2.2, Business is the canonical organizational boundary and external entities are modelled as Business Relationships.
                Legacy <code>Client</code> records are preserved for backward compatibility and can be linked to corresponding canonical Businesses.
              </p>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={loadLegacyMigration} disabled={legacyLoading}>
              {legacyLoading ? 'Refreshing...' : 'Refresh Records'}
            </button>
          </div>

          {legacyLoading ? (
            <p className="admin-muted">Loading legacy client records...</p>
          ) : legacyClients.length === 0 ? (
            <div className="empty-state-box">
              <p>No legacy Client records found in the database.</p>
            </div>
          ) : (
            <div className="legacy-clients-layout">
              <div className="legacy-clients-table-card">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Client Name</th>
                      <th>Code / Status</th>
                      <th>Linked Business Relationship</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legacyClients.map((client) => {
                      const linkedBiz = businesses.find((b) =>
                        String(b._id) === String(client.relationshipBusinessId?._id || client.relationshipBusinessId)
                      );
                      const isLinked = Boolean(linkedBiz);
                      const selectedBiz = selectedBizForClient[client._id] || '';

                      return (
                        <tr key={client._id} className={isLinked ? 'client-row-linked' : 'client-row-unlinked'}>
                          <td>
                            <strong>{client.name}</strong>
                            {client.company && client.company !== client.name && (
                              <div className="admin-muted text-xs">{client.company}</div>
                            )}
                            {client.email && <div className="admin-muted text-xs">{client.email}</div>}
                          </td>
                          <td>
                            <code>{client.clientCode || 'N/A'}</code>
                            <div>
                              <span className={`status-pill ${client.status || 'active'}`}>{client.status || 'active'}</span>
                            </div>
                          </td>
                          <td>
                            {isLinked ? (
                              <div className="linked-biz-badge">
                                <span className="badge-icon">🏢</span>
                                <div>
                                  <strong>{linkedBiz.name}</strong>
                                  <span className="admin-muted text-xs"> ({linkedBiz.businessType || 'business'})</span>
                                </div>
                              </div>
                            ) : (
                              <div className="link-selector-row">
                                <select
                                  className="form-control form-control-sm select-biz-dropdown"
                                  value={selectedBiz}
                                  onChange={(e) =>
                                    setSelectedBizForClient((prev) => ({
                                      ...prev,
                                      [client._id]: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">-- Select Canonical Business --</option>
                                  {businesses.map((b) => (
                                    <option key={b._id} value={b._id}>
                                      {b.name} ({b.businessType || 'business'})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </td>
                          <td>
                            {isLinked ? (
                              <button
                                className="btn btn-xs btn-outline-danger"
                                disabled={linkingClientId === client._id}
                                onClick={() => handleUnlinkClientFromBusiness(client._id)}
                              >
                                {linkingClientId === client._id ? 'Unlinking...' : 'Unlink'}
                              </button>
                            ) : (
                              <button
                                className="btn btn-xs btn-primary"
                                disabled={!selectedBiz || linkingClientId === client._id}
                                onClick={() => handleLinkClientToBusiness(client._id, selectedBiz)}
                              >
                                {linkingClientId === client._id ? 'Linking...' : 'Link to Business'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}